import { getKeystoneDescriptor, getKeystoneIconPath, getDrugIconPaths } from '../../data/keystoneCards';
import { DRUG_ICONS } from '../../data/drugIcons';

function titleCase(text) {
  if (!text) return '';
  return String(text)
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .map((word) => {
      if (!word) return '';
      if (word === word.toUpperCase() && word.length <= 5) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

function mapStatus(stmStatus) {
  switch (stmStatus) {
    // Edward 2026-05-28: green check is reserved for EQUIPPED (live in loadout).
    // 'unlocked' (claimed but not currently slotted) drops to 'active' so the
    // node still shows in its handler color but without the completion check —
    // visual contract is "you have this in inventory, just not equipped."
    case 'equipped':
      return 'completed';
    case 'unlocked':
    case 'pursuable':
    case 'unlockable-now':
      return 'active';
    case 'locked-by-level':
    case 'locked-by-foreign-keystone':
    // No achievement criterion authored on the node yet — backend returns
    // 'locked-unauthored' so the lock glyph fires. As soon as admin authors a
    // criterion, status flips to 'pursuable' and the lock clears.
    case 'locked-unauthored':
    default:
      return 'locked';
  }
}

export function handlerColor(handler) {
  switch (handler) {
    case 'card_unlock':
      return '#fbbf24';
    case 'passive_modifier':
      return '#34d399';
    case 'xp_tag':
      return '#60a5fa';
    case 'keystone_marker':
      return '#f87171';
    default:
      return '#94a3b8';
  }
}

export function skillTreeLayoutMetrics(graph, slotW, slotH, orbTrayReservePx) {
  if (!graph || !graph.nodes || graph.nodes.length === 0) return null;
  if (!slotW || !slotH) return null;

  const vbW = slotW;
  const vbH = slotH;
  const xs = graph.nodes.map((node) => Number(node.x)).filter(Number.isFinite);
  const ys = graph.nodes.map((node) => Number(node.y)).filter(Number.isFinite);
  if (!xs.length || !ys.length) return null;

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const srcW = Math.max(1, maxX - minX);
  const srcH = Math.max(1, maxY - minY);

  const PAD_X = 90;
  // 2026-05-24 (Edward "stretch hex up but Acet stays clear of the top
  // achievement bar"): 30-50 was too tight (Acet rendered behind the bar).
  // 70-100 band lands the Acet circle just below the bar's bottom edge
  // with a small visual breath. Still much tighter than the 90-180 pre-hex
  // band, so the hex stretches up noticeably from its prior layout.
  const PAD_TOP = Math.round(Math.max(70, Math.min(100, vbH * 0.1)));
  // Keystone marker sits this many px above the orb-tray top. NODE_R=22 +
  // pulse extends to ~33px; label (at center+40) extends ~42px below center.
  // KEYSTONE_RESERVE=22 keeps the label baseline ~3-7px above the orb-tray
  // top across desktop/laptop/tablet (verified 2026-05-24 via Playwright at
  // 1920x1080, 1280x800, 1024x768 — gap empirically 4-7px). Circle bottom
  // sits ~11px above tray (very safe). Don't drop below ~18 or the label
  // starts clipping into the orb-tray surface.
  // 2026-05-28 — viewport-proportional node scaling REVERTED per Edward.
  // Previous formula `clamp(1.0, 1.5, vbW/1280)` blew nodes up to 1.5x on
  // 1440p displays which Edward wanted shrunk back to the tablet baseline.
  // Kept as a no-op constant so downstream code (MapNode `node._visualScale`,
  // KEYSTONE_RESERVE math, layout metrics consumers) still gets a defined
  // value; flipping the constant is the single knob if we ever want scaling
  // back. Sub-baseline shrinkage is NOT supported — always 1.0.
  const nodeVisualScale = 1.0;
  const KEYSTONE_RESERVE = 22;
  const orbTrayPx = Math.max(0, orbTrayReservePx || 0);
  const PAD_BOTTOM = orbTrayPx + KEYSTONE_RESERVE * 2;
  const targetW = Math.max(1, vbW - 2 * PAD_X);
  const targetH = Math.max(1, vbH - PAD_TOP - PAD_BOTTOM);
  const scaleX = targetW / srcW;
  const scaleY = targetH / srcH;

  return {
    vbW,
    vbH,
    minX,
    minY,
    maxX,
    maxY,
    srcW,
    srcH,
    padX: PAD_X,
    padTop: PAD_TOP,
    padBottom: PAD_BOTTOM,
    targetW,
    targetH,
    scaleX,
    scaleY,
    orbTrayPx,
    keystoneReserve: KEYSTONE_RESERVE,
    nodeVisualScale,
  };
}

export function inverseTransform(graph, slotW, slotH, renderedX, renderedY, orbTrayReservePx = 0) {
  const metrics = skillTreeLayoutMetrics(graph, slotW, slotH, orbTrayReservePx);
  if (!metrics) {
    return {
      x: Number(renderedX) || 0,
      y: Number(renderedY) || 0,
    };
  }
  return {
    x: metrics.minX + (Number(renderedX) - metrics.padX) / metrics.scaleX,
    y: metrics.minY + (Number(renderedY) - metrics.padTop) / metrics.scaleY,
  };
}

export function forwardTransform(graph, slotW, slotH, substrateX, substrateY, orbTrayReservePx = 0) {
  const metrics = skillTreeLayoutMetrics(graph, slotW, slotH, orbTrayReservePx);
  if (!metrics) {
    return {
      x: Number(substrateX) || 0,
      y: Number(substrateY) || 0,
    };
  }
  return {
    x: metrics.padX + (Number(substrateX) - metrics.minX) * metrics.scaleX,
    y: metrics.padTop + (Number(substrateY) - metrics.minY) * metrics.scaleY,
  };
}

/**
 * Mirror of SkillTreeNodeToast::resolveDrugSlug — keeps the on-canvas
 * ownership signal in lockstep with what the click-through toast renders.
 * Operates on the raw stm-graph node (pre-mapping), where `payloadData` and
 * `id` are top-level fields.
 */
function resolveStmNodeSlug(stmNode) {
  if (!stmNode) return null;
  const payload = stmNode.payloadData || stmNode.payload?.data;
  return (
    payload?.drug_slug ||
    payload?.card_slug ||
    stmNode.id ||
    null
  );
}

/**
 * Canonicalize a drug slug / generic-name for equality matching. equip_card
 * writes generic-name form ("sodium bicarbonate") into equipped_drugs while
 * node payloads carry slug form ("sodium_bicarbonate"); collapse whitespace and
 * hyphens to single underscores (+ lowercase) so both forms compare equal.
 */
export function normalizeOwnableSlug(value) {
  return String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}

/**
 * Canonical "owned" set from the reservoir. A drug is considered owned for
 * skill-tree-checkmark purposes when its reservoir entry is
 * `user_status === 'golden'` or it's the player's chosen starter — same
 * predicate the detail toast uses. Keys are lowercased `generic_name`s; the
 * skill-tree node's drug slug matches the lowercase generic_name (the toast
 * uses the same case-insensitive lookup).
 */
export function buildOwnedSlugSet(reservoir) {
  const set = new Set();
  if (!Array.isArray(reservoir)) return set;
  for (const entry of reservoir) {
    if (!entry) continue;
    const owned = entry.user_status === 'golden' || entry.is_chosen_starter;
    if (!owned) continue;
    const gen = entry.generic_name;
    if (gen) set.add(String(gen).toLowerCase());
  }
  return set;
}

// Non-selected keystones render in this muted gray so only the equipped
// keystone shows its accent identity on the tree. (Edward 2026-06-09)
const KEYSTONE_INACTIVE_COLOR = '#6b7280';

// Warfarin hub gets a distinct purple identity (universal augment-stub hub).
const WARFARIN_HUB_COLOR = '#a855f7';

/**
 * The SINGLE source of truth for a skill-tree node's identity color.
 *
 * "Identity" = the node's intrinsic affinity color, independent of equipped /
 * owned status (the green "completed" override is applied separately at render
 * time in ProgressMap, gated on `keepAccent`). The hierarchy, highest priority
 * first:
 *   1. Keystone   → its registry accent when equipped, else muted gray.
 *   2. Substrate-authored accent (`metadata.accent` / top-level `color` /
 *      `payloadData.accent`) — e.g. Psilocybin purple.
 *   3. Warfarin hub (`slot === 'warfarin'`) → purple.
 *   4. Connector (`slot` starts with `connector_`, has `branch_of`) → inherits
 *      the parent keystone's accent so the affinity reads at a glance.
 *   5. Default → `handlerColor(payloadHandler)` (card_unlock = gold, etc.).
 *
 * Operates on a NORMALIZED graph node (camelCase `payloadHandler`/`payloadData`,
 * raw `slot`/`branch_of`/`metadata`/`color` preserved by collectNodes). Pulled
 * out of skillTreeToMapData so the equip hotbar can color each loadout atom by
 * the exact same rule the tree uses — change the tree's coloring and the hotbar
 * follows automatically. (Edward 2026-06-09 — "robustly associate the color
 * with the drug".)
 */
export function resolveNodeIdentityColor(node, equippedKeystoneSlug) {
  if (!node) return handlerColor(null);
  const isKeystone = node.type === 'keystone';
  const keystoneSlug = isKeystone
    ? (node.payloadData?.keystone_slug || node.id)
    : null;
  const keystoneDescriptor = keystoneSlug ? getKeystoneDescriptor(keystoneSlug) : null;
  if (keystoneDescriptor) {
    const isEquipped = String(keystoneSlug).toLowerCase()
      === String(equippedKeystoneSlug || '').toLowerCase();
    return isEquipped ? keystoneDescriptor.accent : KEYSTONE_INACTIVE_COLOR;
  }
  const substrateAccent = node.metadata?.accent || node.color || node.payloadData?.accent || null;
  if (substrateAccent) return substrateAccent;
  if (!isKeystone && node.slot === 'warfarin') return WARFARIN_HUB_COLOR;
  const isConnector = !isKeystone
    && typeof node.slot === 'string'
    && node.slot.startsWith('connector_');
  const connectorParent = isConnector && node.branch_of
    ? getKeystoneDescriptor(node.branch_of)
    : null;
  if (connectorParent) return connectorParent.accent;
  return handlerColor(node.payloadHandler);
}

/**
 * Map<normalized-drug-slug → identity color> for every non-keystone card node
 * in a normalized skill-tree graph. The equip hotbar consumes this to color each
 * equipped drug atom by its tree identity instead of a flat green. Keyed via
 * normalizeOwnableSlug so generic-name ("sodium bicarbonate") and slug
 * ("sodium_bicarbonate") forms both resolve.
 */
export function buildDrugColorMap(graph, equippedKeystoneSlug) {
  const map = new Map();
  if (!graph || !Array.isArray(graph.nodes)) return map;
  for (const node of graph.nodes) {
    if (node.type === 'keystone') continue;
    const slug = resolveStmNodeSlug(node);
    if (!slug) continue;
    map.set(normalizeOwnableSlug(slug), resolveNodeIdentityColor(node, equippedKeystoneSlug));
  }
  return map;
}

export function skillTreeToMapData(graph, slotW, slotH, equippedKeystoneSlug, orbTrayReservePx, ownedSlugs, injectHtnPortal = false, suppressKeystoneMarker = false, restrictSlugs = null) {
  const metrics = skillTreeLayoutMetrics(graph, slotW, slotH, orbTrayReservePx);
  if (!metrics) return null;

  // Equipped/owned slugs arrive in either slug form ("sodium_bicarbonate", from
  // the node payload's drug_slug) or generic-name form ("sodium bicarbonate",
  // which is what equip_card writes into equipped_drugs). Collapse whitespace +
  // hyphens to underscores on BOTH sides so the green "equipped" check matches
  // regardless of representation. Bicarbonate was the only multi-word equippable
  // drug, so it was the only node that silently failed to flip to 'completed'
  // on equip while every single-token drug matched by luck. Same component backs
  // the in-game tree and the admin harness, so this fixes both.
  const ownedLookup = ownedSlugs instanceof Set
    ? new Set([...ownedSlugs].map(normalizeOwnableSlug))
    : new Set();
  const substrateKeystoneSlugs = new Set();
  const nodes = graph.nodes.map((node) => {
    // HTN "Regimen" portal (region:"main") and the HTN region BOSS (region:"htn")
    // are non-drug map nodes — short-circuit the drug-card pipeline (no
    // slug/glyph/keystone logic applies). The portal is the real definition node
    // published in Phase A; the boss is synthesized by the viewport's HTN layout.
    // ProgressMap already renders both types (htn_portal / htn_boss). Clicks are
    // routed in SkillTreeViewport.handleNodeClick.
    if (node.type === 'htn_portal' || node.type === 'htn_boss') {
      const isBoss = node.type === 'htn_boss';
      return {
        id: node.id,
        x: metrics.padX + (node.x - metrics.minX) * metrics.scaleX,
        y: metrics.padTop + (node.y - metrics.minY) * metrics.scaleY,
        _visualScale: metrics.nodeVisualScale,
        type: node.type,
        region: node.region || (isBoss ? 'htn' : 'main'),
        target_region: node.metadata?.target_region || (isBoss ? undefined : 'htn'),
        status: 'active',
        label: node.label || (isBoss ? 'Resistant HTN' : 'Antihypertensives'),
        short_label: node.short_label || (isBoss ? 'Boss' : 'Antihypertensives'),
        no_truncate: true,
        _stm: {
          status: isBoss ? 'boss' : 'portal',
          payloadHandler: node.type,
          description: node.description || node.metadata?.effect_description
            || (isBoss
              ? 'Fight the resistant-hypertension boss with your equipped regimen.'
              : 'Explore the antihypertensives: ACE inhibitors, ARBs, CCBs, beta blockers, and thiazides.'),
        },
      };
    }
    // Ownership override — when the player owns the card this node grants
    // (reservoir user_status='golden' OR chosen starter), promote ProgressMap
    // status to 'completed'. That cascades into ProgressMap's canonical green
    // styling: stroke + fill flip to GREEN, inner glow renders at 20% opacity
    // GREEN, and the white checkmark path draws. Only meaningful for
    // card_unlock nodes — passive/xp/keystone handlers don't grant cards.
    const slug = resolveStmNodeSlug(node);
    const isOwned = !!(
      node.payloadHandler === 'card_unlock' &&
      slug &&
      ownedLookup.has(normalizeOwnableSlug(slug))
    );
    const baseStatus = mapStatus(node.status);
    const status = isOwned ? 'completed' : baseStatus;
    const isKeystone = node.type === 'keystone';
    // Substrate-defined keystone nodes (v2+ skill tree spec) get the same
    // visual treatment as the synthetic equipped-keystone node below: icon
    // path, accent color, and "AbilityName — DrugClass" tooltip pulled from
    // the shared descriptor in data/keystoneCards.jsx. Lookup precedence is
    // payloadData.keystone_slug → node.id, mirroring the substrate shape.
    const keystoneSlug = isKeystone
      ? (node.payloadData?.keystone_slug || node.id)
      : null;
    const keystoneDescriptor = keystoneSlug ? getKeystoneDescriptor(keystoneSlug) : null;
    if (keystoneSlug) substrateKeystoneSlugs.add(String(keystoneSlug).toLowerCase());
    // Connector nodes (v2+ slot='connector_*' with branch_of=<keystone>) are
    // the keystone-unique "first card unlocked when this keystone is equipped"
    // cards (Digoxin/Vanc, Albuterol/Adali, Bicarbonate/Met). They render in
    // the parent keystone's accent color so the affinity is visible at a
    // glance. Branch + branch_chain cards stay in the default card_unlock
    // gold to preserve the visual hierarchy (keystone → connector affinity →
    // branch progression).
    const isConnector = !isKeystone
      && typeof node.slot === 'string'
      && node.slot.startsWith('connector_');
    const connectorParentDescriptor = isConnector && node.branch_of
      ? getKeystoneDescriptor(node.branch_of)
      : null;
    // Warfarin hub — v5 designates Warfarin as the universal augment-stub
    // unlock hub. It's the only card_unlock node that gates ALL three
    // augment stubs across every keystone, so it gets a distinct purple
    // identity and a larger radius via the prominent_hub flag (ProgressMap
    // MapNode honors this in its baseR + outer-ring logic).
    const isWarfarinHub = !isKeystone && node.slot === 'warfarin';
    // Substrate-authored accent override. A node can declare its own identity
    // color via metadata.accent (or top-level `color`) and the canvas honors it
    // ahead of the default handler gold — used for off-tree consumable nodes
    // like Psilocybin (purple) that don't belong to a keystone affinity.
    const substrateAccent = node.metadata?.accent || node.color || node.payloadData?.accent || null;
    // Existing per-slug stroke glyph (the original node icon). Node-id is tried
    // FIRST so a collision-renamed node whose id differs from its drug_slug can
    // carry a bespoke glyph. The drug slug remains the generic fallback.
    const strokePaths = (!isKeystone && slug)
      ? (getDrugIconPaths(node.id) || getDrugIconPaths(slug))
      : null;
    const hasStroke = Array.isArray(strokePaths) && strokePaths.length > 0;
    // Filled recraft silhouette is APPENDED ONLY to nodes that have no existing
    // stroke icon (the gaps) — nodes that already had an icon keep it. The data
    // module's viewBox is the tight content box "minX minY w h" on a 2048 source
    // canvas — convert to the {paths, viewBox, bbox} shape MapNode's fit wants.
    const drugIconEntry = (!isKeystone && slug && !hasStroke) ? DRUG_ICONS[slug] : null;
    let fillGlyph = null;
    if (drugIconEntry) {
      const [mnX, mnY, w, h] = String(drugIconEntry.viewBox).split(/\s+/).map(Number);
      fillGlyph = { paths: drugIconEntry.paths, viewBox: 2048, bbox: { minX: mnX, minY: mnY, w, h } };
    }
    const isEquippedKeystone = !!keystoneDescriptor
      && String(keystoneSlug).toLowerCase() === String(equippedKeystoneSlug || '').toLowerCase();
    // Forced-equip gate restriction (standalone /play/hypertension guided first
    // pick): when `restrictSlugs` is provided, the HTN flower is narrowed to a
    // whitelist of starter drugs (one per axis). Non-whitelisted DRUG cards are
    // forced 'locked' — ProgressMap dims + padlocks them AND the node toast hides
    // its equip button (equipReady only fires for unlocked/unlockable/pursuable).
    // The whitelisted few are lit + equip-ready and flagged `isStarterDrug` so
    // ProgressMap draws an axis-colored highlight ring. Keystones and non-drug
    // nodes (no slug) are never restricted. Fully no-op when restrictSlugs is null
    // (the normal tree). Two DIFFERENT status vocabularies must both be overridden:
    // the top-level `status` (ProgressMap dim/lock — 'active' lit, 'locked' dim),
    // and `_stm.status` (the node toast's equip gating — 'pursuable' shows the
    // equip button, 'locked' hides it). So a lit starter needs top-level 'active'
    // + _stm 'pursuable'; a suppressed drug needs 'locked' for both.
    let isStarterDrug = false;
    let restrictedStatus = null;      // top-level (ProgressMap)
    let restrictedStmStatus = null;   // raw _stm (toast equip gating)
    if (restrictSlugs && !isKeystone && slug) {
      if (restrictSlugs.has(normalizeOwnableSlug(slug))) {
        restrictedStatus = 'active';
        restrictedStmStatus = 'pursuable';
        isStarterDrug = true;
      } else {
        restrictedStatus = 'locked';
        restrictedStmStatus = 'locked';
      }
    }
    const effectiveStatus = restrictedStatus || (isEquippedKeystone ? 'active' : status);
    const effectiveStmStatus = restrictedStmStatus || node.status;
    return {
      id: node.id,
      // Region passthrough so downstream (SkillTreeViewport filter, node toast
      // anon-equip detection) can tell HTN-region cards from the main tree.
      region: node.region || 'main',
      x: metrics.padX + (node.x - metrics.minX) * metrics.scaleX,
      y: metrics.padTop + (node.y - metrics.minY) * metrics.scaleY,
      _visualScale: metrics.nodeVisualScale,
      type: isKeystone ? 'keystone' : 'topic',
      status: effectiveStatus,
      // Starter-drug highlight (forced-equip gate): the whitelisted axis reps get
      // an axis-colored ring in ProgressMap. Undefined/false everywhere else.
      isStarterDrug,
      // Line-of-therapy luminance (A1) — numeric class-band opacity set upstream
      // in computeHtnRegionLayout (HTN region only). Forwarded so ProgressMap's
      // node opacity fallback can dim later-line drug cards. Undefined elsewhere.
      importance: node.importance,
      // HTN cards whose id collided with a main-tree slug may carry an `htn_`
      // prefix. node.label falls back to the id, so
      // strip the prefix here to avoid rendering "Htn Propranolol". Prefer the
      // authored display name / card name when present. No-op for every other node.
      label: keystoneDescriptor
        ? keystoneDescriptor.genericName
        : titleCase(
            node.metadata?.display_name
              || node.payload?.data?.card_name
              || String(node.label ?? '').replace(/^htn_/, ''),
          ),
      // Identity color resolved by the shared rule (keystone accent / authored
      // accent / warfarin purple / connector-keystone affinity / handler gold).
      // Same helper the equip hotbar uses, so the two never drift. The green
      // "completed/equipped" override is applied downstream in ProgressMap.
      color: resolveNodeIdentityColor(node, equippedKeystoneSlug),
      iconPath: keystoneDescriptor ? getKeystoneIconPath(keystoneDescriptor.id) : node.iconPath,
      // Effect-communicating glyph for non-keystone drug cards (multi-stroke
      // array; color comes from the node's keystone-affinity `color` above).
      // Keyed by the resolved drug_slug so it matches the card the node grants.
      // Suppressed when a filled silhouette glyph is present for this node.
      iconPaths: hasStroke ? strokePaths : null,
      iconFillPaths: fillGlyph ? fillGlyph.paths : null,
      iconFillViewBox: fillGlyph ? fillGlyph.viewBox : null,
      iconFillBBox: fillGlyph ? fillGlyph.bbox : null,
      // Authored-accent nodes keep their identity color in every state (the
      // equipped white-check still overlays) instead of flipping to the global
      // green "equipped" fill — so Psilocybin reads as a purple node throughout.
      // Keystones also keep their own color so a non-selected (owned) keystone
      // stays gray instead of flipping to the owned-green fill.
      keepAccent: !!substrateAccent || !!keystoneDescriptor,
      prominent_hub: isWarfarinHub,
      _stm: {
        status: effectiveStmStatus,
        payloadHandler: node.payloadHandler,
        payloadData: node.payloadData,
        progress: node.progress,
        nodeState: node.nodeState,
        description: keystoneDescriptor
          ? `${keystoneDescriptor.abilityName}: ${keystoneDescriptor.drugClass}`
          : connectorParentDescriptor
            ? `${node.description || node.summary || ''}${node.description || node.summary ? '. ' : ''}${connectorParentDescriptor.genericName} connector`
            : (node.description || node.summary),
      },
    };
  });

  // Synthetic equipped-keystone node — appended ONLY when the substrate
  // doesn't already define a keystone node for the equipped slug. Without
  // this guard, substrate-defined keystones (v2+) render twice at the
  // bottom-center, once from the substrate and once from this append.
  const equippedSlugLower = equippedKeystoneSlug
    ? String(equippedKeystoneSlug).toLowerCase()
    : null;
  const keystone = getKeystoneDescriptor(equippedKeystoneSlug);
  // Suppressed in the HTN region — that map has its own bottom-center focal node
  // (the resistant-HTN boss), and the keystone marker would collide with it. The
  // equipped keystone still applies to the regimen fight; it's just not drawn here.
  if (keystone && !suppressKeystoneMarker && !(equippedSlugLower && substrateKeystoneSlugs.has(equippedSlugLower))) {
    nodes.push({
      id: `keystone__${keystone.id}`,
      x: metrics.vbW / 2,
      y: metrics.vbH - (metrics.orbTrayPx + metrics.keystoneReserve),
      _visualScale: metrics.nodeVisualScale,
      type: 'keystone',
      status: 'active',
      label: keystone.genericName,
      color: keystone.accent,
      iconPath: getKeystoneIconPath(keystone.id),
      _stm: {
        status: 'keystone',
        payloadHandler: 'keystone_marker',
        description: `${keystone.abilityName}: ${keystone.drugClass}`,
      },
    });
  }

  // Synthetic HTN "Regimen" portal — a single bespoke gateway node appended
  // ONLY for the logged-in dashboard skill-tree mount (SkillTreeViewport passes
  // injectHtnPortal=true when its onPortal handler is wired). It is deliberately
  // NOT injected for the anon Landing, the admin harness, or the authoring
  // canvas, all of which share this same layout fn but pass no flag. Position:
  // upper-right margin (node-free — real nodes are inset by padX on both sides)
  // and just below the top achievement-bar band (padTop), so it never overlaps a
  // real tree node nor hides behind the bar. Its click is routed to onPortal()
  // in SkillTreeViewport.handleNodeClick (it never pins a toast / mutates loadout).
  // Legacy synthetic fallback — only inject when the active definition has NO
  // real __htn_portal__ node (older published versions predating the Phase-A
  // publish). When the real portal is present it flows through the map() above,
  // so this guard prevents a duplicate gateway.
  if (injectHtnPortal && !nodes.some((n) => n.id === '__htn_portal__')) {
    nodes.push({
      id: '__htn_portal__',
      x: metrics.vbW - metrics.padX * 0.5,
      y: metrics.padTop + 36,
      _visualScale: metrics.nodeVisualScale,
      type: 'htn_portal',
      status: 'active',
      label: 'Antihypertensives',
      short_label: 'Antihypertensives',
      no_truncate: true,
      _stm: {
        status: 'portal',
        payloadHandler: 'htn_portal',
        description: 'Explore the antihypertensives: ACE inhibitors, ARBs, CCBs, beta blockers, and thiazides.',
      },
    });
  }

  const edges = graph.edges.map((edge) => ({
    from: edge.from,
    to: edge.to,
    type: 'synthesis',
  }));

  return {
    nodes,
    edges,
    layout: 'radial',
    fixedWidth: metrics.vbW,
    fixedHeight: metrics.vbH,
  };
}

// Clinical left-to-right class order for the HTN region columns (RAAS -> MRA ->
// diuretics -> CCBs -> beta blockers). Classes not listed fall in after these.
export const HTN_CLASS_ORDER = [
  'ace', 'arb', 'k_sparing_mra', 'carbonic_anhydrase', 'osmotic', 'thiazide', 'loop',
  'dhp_ccb', 'nondhp_ccb', 'beta_blocker',
];

// Semantic per-class accent for the HTN region drug nodes. Follows the main
// tree's "color = affinity, never decorative" rule (Debono design principle 3):
// RAAS classes read cool (blue → violet), diuretics teal/cyan, calcium warm
// (amber/orange), sympathetic red. Applied via metadata.accent so
// resolveNodeIdentityColor picks it up exactly like Psilocybin's purple —
// keeping the two glyph/color systems (skill tree here vs drug-map recraft
// fills in drugIcons.js) cleanly separate. Keyed by htn_class.
// A class MISSING from this map is not cosmetically neutral: resolveNodeIdentityColor
// falls all the way through to handlerColor('card_unlock') = #fbbf24 AMBER, so the drug
// reads as a generic unlock in the tree AND in the equip hotbar (which colors each
// equipped atom from buildDrugColorMap). That is what made acetazolamide and mannitol
// show up yellow next to their teal diuretic siblings. Every htn_class must be listed.
export const HTN_CLASS_ACCENT = {
  ace: '#3b82f6',
  arb: '#60a5fa',
  k_sparing_mra: '#8b5cf6',
  // Upstream nephron segments, deeper than their downstream siblings so the whole
  // diuretic family still reads teal/cyan while proximal-to-distal stays legible:
  // proximal CAI (teal-600) -> descending osmotic (cyan-600) -> distal thiazide
  // (teal-500) -> ascending loop (cyan-500).
  carbonic_anhydrase: '#0d9488',
  osmotic: '#0891b2',
  thiazide: '#14b8a6',
  loop: '#06b6d4',
  dhp_ccb: '#f59e0b',
  nondhp_ccb: '#f97316',
  beta_blocker: '#ef4444',
};

// LINE-OF-THERAPY LUMINANCE (Workstream A1) — node-body opacity encodes each
// class's CLINICAL rank so first-line drugs read brighter than later-line ones,
// making the escalation ladder legible at a glance. This is NOT node.tier:
// node.tier is the drug's class-cluster LAYOUT order (ace=1 ... thiazide=6 ...
// mra=8), which is not a clinical ranking — driving opacity off it would wrongly
// dim thiazides (first-line) and brighten beta-blockers. So the clinical bands
// are authored explicitly here (Edward 2026-07-08, ACC/AHA 2017 3-band):
//   FIRST-LINE  1.00  thiazide, ace, arb, dhp_ccb    (the guideline "big four")
//   SECOND-LINE 0.82  beta_blocker, nondhp_ccb, k_sparing_mra (compelling-indication / resistant-HTN add-ons)
//   ADJUNCT     0.66  loop                           (volume/CKD/HF, not primary-HTN first-line)
// Consumed via metadata.importance → ProgressMap's node opacity fallback (it only
// ranks reachable/available cards; locked + unavailable states still win the dim
// ladder). Keyed by htn_class, mirroring HTN_CLASS_ACCENT. Unlisted classes stay
// full-bright (no false dimming).
export const HTN_CLASS_LUMINANCE = {
  thiazide: 1.0,
  ace: 1.0,
  arb: 1.0,
  dhp_ccb: 1.0,
  beta_blocker: 0.82,
  nondhp_ccb: 0.82,
  k_sparing_mra: 0.82,
  loop: 0.66,
  // Acetazolamide and mannitol are not antihypertensives at all — they earn their
  // place on this tree as nephron-segment teaching, so they sit in the same ADJUNCT
  // band as the loops. Listing them matters: an unlisted class stays FULL-BRIGHT,
  // which would have made the two least-relevant drugs the most prominent nodes in
  // the diuretic lobe.
  carbonic_anhydrase: 0.66,
  osmotic: 0.66,
};

// Curated QUATREFOIL-FLOWER node positions (authored space, y increases DOWNWARD
// on screen — larger y is lower). The HTN region is a SKILL-TREE region, NOT a
// combat map (Edward 2026-07-02: "the map it should lead to is NOT a combat map
// ... idk why it has an enemy in it, just focus on connecting the drugs with a
// nice geometric pattern"). So there is NO boss/enemy node. The shape is a
// four-lobe bloom keyed to the four pharmacologic axes: a RAAS CROWN across the
// top (ACE left / ARB center / MRA right — three triangles), DIURETIC and
// CALCIUM as balanced left/right side lobes, and the seven-strong beta family as
// a 6-around-1 rosette forming the bottom bloom. Symmetric about x=0. Coordinates
// authored y-up in the design panel then flipped into this y-down render space
// (crown small-y = top, bloom large-y = bottom). The bounding box is stretched to
// the viewport by skillTreeLayoutMetrics (X/Y independently). Keyed by drug_slug.
export const HTN_NODE_POS = {
  // RAAS crown — ARB triangle at top-center
  losartan: { x: 0, y: 0.1 },
  olmesartan: { x: -0.5, y: 0.9 },
  valsartan: { x: 0.5, y: 0.9 },
  // RAAS crown — ACE triangle, top-left petal
  lisinopril: { x: -2.0, y: 0.5 },
  enalapril: { x: -2.5, y: 1.25 },
  benazepril: { x: -1.6, y: 1.35 },
  // RAAS crown — K-sparing / MRA triangle, top-right petal
  spironolactone: { x: 2.0, y: 0.5 },
  eplerenone: { x: 2.5, y: 1.25 },
  amiloride: { x: 1.6, y: 1.35 },
  // Left lobe — diuretics (thiazide inner, loop outer)
  hydrochlorothiazide: { x: -2.0, y: 2.9 },
  chlorthalidone: { x: -1.9, y: 3.8 },
  furosemide: { x: -2.9, y: 2.9 },
  // torsemide (left tip) and verapamil (right tip) are the two nodes that set
  // the layout's horizontal extent. They MUST be equidistant from x=0 or the
  // bounding-box centering in skillTreeLayoutMetrics shifts the x=0 spine off
  // the viewport center (torsemide -3.35 vs verapamil +3.15 pushed the spine
  // ~28px right of center — visible against the window-centered banner /
  // empty-state). Balanced to ±3.25 so minX=-maxX and the spine sits dead
  // center (2026-07-08, Edward). Every other node already mirrors about x=0.
  torsemide: { x: -3.25, y: 3.65 },
  bumetanide: { x: -2.5, y: 3.75 },
  // Left lobe — UPSTREAM nephron pair, hung below the loop trio so the diuretic
  // lobe reads proximal-to-distal as one nephron chain instead of two orphans.
  // Both sit strictly INSIDE the existing bounding box (|x| <= 3.25 set by the
  // torsemide/verapamil tips, y <= 6.0 set by the beta bloom) and clear of the
  // bloom's x = -0.82 column. That containment is the whole fix for the cut-off:
  // an unpositioned node fell to the y = -0.8 fallback row ABOVE the flower,
  // which grew the box upward, and since skillTreeLayoutMetrics stretches the box
  // to the viewport, everything below slid out of reach and stopped being
  // selectable. Keeping them in-box leaves the metrics byte-for-byte unchanged.
  mannitol: { x: -3.0, y: 4.6 },
  acetazolamide: { x: -2.15, y: 4.8 },
  // Right lobe — calcium (dhp inner, nondhp outer)
  amlodipine: { x: 2.0, y: 2.9 },
  nifedipine: { x: 1.9, y: 3.8 },
  diltiazem: { x: 2.9, y: 2.95 },
  verapamil: { x: 3.25, y: 3.8 },
  // Bottom bloom — beta-blocker 5-around-1 rosette (metoprolol hub)
  metoprolol: { x: 0, y: 5.5 },
  nebivolol: { x: 0, y: 4.55 },
  atenolol: { x: 0.82, y: 5.0 },
  carvedilol: { x: -0.82, y: 5.0 },
  bisoprolol: { x: 0.82, y: 6.0 },
  labetalol: { x: -0.82, y: 6.0 },
};

// Envelope of the CURATED flower, derived from the map above rather than hardcoded so
// it tracks any future re-authoring. The unplaced-drug fallback in
// computeHtnRegionLayout clamps into these bounds, which is what guarantees a missing
// position can never grow the layout's bounding box (and so can never push the bottom
// of the tree out of reach).
export const HTN_CURATED_BOUNDS = (() => {
  const pts = Object.values(HTN_NODE_POS);
  return {
    minX: Math.min(...pts.map((p) => p.x)),
    maxX: Math.max(...pts.map((p) => p.x)),
    minY: Math.min(...pts.map((p) => p.y)),
    maxY: Math.max(...pts.map((p) => p.y)),
  };
})();

// Quatrefoil mesh edges (curated) — within-class chains color each petal/lobe,
// a central stem (beta hub -> ARB) threads bloom to crown, and inter-lobe links
// weave all four axes into one connected web (like the main tree's edges tracing
// its hexagon). No boss trunks — there is no boss. Rendered as organic bezier
// curves (skillTreeToMapData re-types these 'synthesis', drawn like the main
// tree's dashed arcs).
const HTN_EDGES = [
  // beta rosette — hub spokes + outer ring
  ['metoprolol', 'nebivolol'], ['metoprolol', 'atenolol'],
  ['metoprolol', 'bisoprolol'], ['metoprolol', 'carvedilol'], ['metoprolol', 'labetalol'],
  ['nebivolol', 'atenolol'], ['atenolol', 'bisoprolol'], ['bisoprolol', 'labetalol'],
  ['labetalol', 'carvedilol'], ['carvedilol', 'nebivolol'],
  // central stem — bloom up into the RAAS crown
  ['nebivolol', 'losartan'],
  // RAAS crown — ARB triangle + petals
  ['losartan', 'olmesartan'], ['losartan', 'valsartan'], ['olmesartan', 'valsartan'],
  ['olmesartan', 'lisinopril'], ['valsartan', 'spironolactone'],
  ['lisinopril', 'enalapril'], ['lisinopril', 'benazepril'], ['enalapril', 'benazepril'],
  ['spironolactone', 'eplerenone'], ['spironolactone', 'amiloride'], ['eplerenone', 'amiloride'],
  // crown -> side lobes
  ['benazepril', 'hydrochlorothiazide'], ['amiloride', 'amlodipine'],
  // left lobe — diuretics
  ['hydrochlorothiazide', 'chlorthalidone'], ['hydrochlorothiazide', 'furosemide'],
  ['furosemide', 'torsemide'], ['furosemide', 'bumetanide'], ['torsemide', 'bumetanide'],
  ['chlorthalidone', 'bumetanide'],
  // left lobe — upstream nephron pair woven into the loop trio. The
  // acetazolamide-mannitol edge is anatomically true (proximal tubule feeds the
  // thin descending limb) and both then hand off to the thick ascending limb, so
  // the lobe teaches the nephron in order rather than just looking connected.
  ['acetazolamide', 'mannitol'],
  ['mannitol', 'torsemide'], ['mannitol', 'bumetanide'],
  ['acetazolamide', 'bumetanide'],
  // right lobe — calcium
  ['amlodipine', 'nifedipine'], ['amlodipine', 'diltiazem'],
  ['diltiazem', 'verapamil'], ['nifedipine', 'verapamil'],
  // bloom -> side lobes (tie the flower together)
  ['carvedilol', 'hydrochlorothiazide'], ['atenolol', 'amlodipine'],
];

// Build the HTN region as a SKILL-TREE region (not a combat map): the drug cards
// are arranged into the QUATREFOIL-FLOWER geometry above, each node ringed in its
// semantic class color, and a curated mesh connects them into one web. A single
// synthetic back-portal (top-left) returns to the main tree. There is NO boss /
// enemy node — combat lives on a separate map. The published definition's edges
// all point at the MAIN-region portal, so the filtered region view would
// otherwise have no edges of its own; we synthesize them here. Coords are
// authored space; skillTreeLayoutMetrics normalizes them. Returns {nodes, edges}.
export function computeHtnRegionLayout(htnCardNodes, opts = {}) {
  const backId = opts.backId || '__htn_region_back__';

  const idBySlug = new Map();
  // Any drug missing a curated position still renders (never stacks at the origin),
  // but it must degrade INSIDE the flower's envelope. It used to land on a row at
  // y = -0.8, ABOVE the crown — which grew the layout bounding box upward, and since
  // skillTreeLayoutMetrics stretches that box to the viewport, every node slid down
  // until the bottom of the tree fell under the orb tray and stopped being
  // selectable. One unplaced drug broke the whole map (Edward 2026-07-25:
  // acetazolamide + mannitol). Clamping to the curated bounds means an unplaced drug
  // is now a visible authoring bug instead of an unusable region.
  const missing = htnCardNodes.filter((n) => !HTN_NODE_POS[normalizeOwnableSlug(resolveStmNodeSlug(n))]);
  let fallbackI = 0;

  const nodes = [];
  for (const n of htnCardNodes) {
    const slug = normalizeOwnableSlug(resolveStmNodeSlug(n));
    if (slug) idBySlug.set(slug, n.id);
    const cls = String(n?.metadata?.htn_class || 'other').toLowerCase();
    let pos = HTN_NODE_POS[slug];
    if (!pos) {
      const total = Math.max(1, missing.length);
      const span = HTN_CURATED_BOUNDS.maxX - HTN_CURATED_BOUNDS.minX;
      pos = {
        x: HTN_CURATED_BOUNDS.minX + (span * (fallbackI + 0.5)) / total,
        y: HTN_CURATED_BOUNDS.maxY,
      };
      fallbackI += 1;
    }
    nodes.push({
      ...n,
      x: pos.x,
      y: pos.y,
      // Line-of-therapy luminance (A1) — numeric class-band opacity read by
      // ProgressMap's node opacity fallback. Undefined for unlisted classes so
      // they stay full-bright. Carried top-level (skillTreeToMapData forwards it).
      importance: HTN_CLASS_LUMINANCE[cls] ?? n.importance,
      // Semantic class ring color — read by resolveNodeIdentityColor as a
      // substrate accent (same path as Psilocybin's purple).
      metadata: {
        ...(n.metadata || {}),
        accent: HTN_CLASS_ACCENT[cls] || n.metadata?.accent || null,
      },
    });
  }

  // Top-left back-portal to the main tree (the only non-drug node in the region).
  // Omitted when the region is locked (opts.omitBack) — the standalone /play demo
  // stays focused on the antihypertensives area with no door back to the full tree.
  if (!opts.omitBack) {
    nodes.push({
      id: backId,
      type: 'htn_portal',
      region: 'htn',
      x: -3.2,
      y: 0.2,
      label: 'Back to Tree',
      short_label: 'Back',
      no_truncate: true,
      metadata: { target_region: 'main', back: true },
    });
  }

  const edges = [];
  for (const [a, b] of HTN_EDGES) {
    const from = idBySlug.get(a);
    const to = idBySlug.get(b);
    if (from && to) edges.push({ from, to, type: 'connector' });
  }

  return { nodes, edges };
}
